import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtMoney, formatApiError, DOC_TYPE_META } from "@/lib/api";
import { PageHeader, Content, StatusBadge } from "@/components/PageChrome";
import { ArrowLeft, Download, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";

export default function ClientStatement() {
  const { id } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/clients/${id}/statement`).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [id]);

  if (!data) {
    return (
      <>
        <PageHeader overline="Client" title="Statement" testid="page-statement" />
        <Content><div className="text-sm text-gray-500">Loading…</div></Content>
      </>
    );
  }

  const { client, totals, timeline } = data;
  const currency = timeline[0]?.currency || "INR";

  return (
    <>
      <PageHeader
        overline="Client Statement"
        title={client.name}
        description={[client.email, client.city, client.gstin && `GSTIN ${client.gstin}`].filter(Boolean).join(" · ")}
        testid="page-statement"
        actions={
          <Link to="/app/clients" className="text-sm px-3 h-10 border border-[var(--border)] rounded-md inline-flex items-center gap-2 hover:bg-white" data-testid="btn-back-clients">
            <ArrowLeft className="w-4 h-4" /> Back to Clients
          </Link>
        }
      />
      <Content>
        {/* Totals */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="card-flat p-5" data-testid="stmt-invoiced">
            <div className="overline flex items-center gap-1.5"><TrendingUp className="w-3 h-3" /> Invoiced</div>
            <div className="text-2xl font-medium mt-3 num">{fmtMoney(totals.invoiced, currency)}</div>
          </div>
          <div className="card-flat p-5" data-testid="stmt-received">
            <div className="overline flex items-center gap-1.5"><TrendingDown className="w-3 h-3 text-green-600" /> Received</div>
            <div className="text-2xl font-medium mt-3 num text-green-700">{fmtMoney(totals.received, currency)}</div>
          </div>
          <div className="card-flat p-5" data-testid="stmt-credited">
            <div className="overline">Credited</div>
            <div className="text-2xl font-medium mt-3 num text-blue-700">{fmtMoney(totals.credited, currency)}</div>
          </div>
          <div className="card-flat p-5 border-l-4 border-l-amber-500" data-testid="stmt-outstanding">
            <div className="overline">Outstanding</div>
            <div className={`text-2xl font-medium mt-3 num ${totals.outstanding > 0 ? "text-amber-700" : "text-green-700"}`}>{fmtMoney(totals.outstanding, currency)}</div>
          </div>
        </div>

        {/* Timeline */}
        {timeline.length === 0 ? (
          <div className="card-flat p-12 text-center text-sm text-gray-500" data-testid="stmt-empty">
            No documents for this client yet.
          </div>
        ) : (
          <div className="card-flat overflow-hidden">
            <div className="px-5 py-4 border-b border-[var(--border)] flex items-center justify-between">
              <div>
                <div className="overline">Timeline</div>
                <div className="text-lg font-medium mt-0.5">All documents · {timeline.length}</div>
              </div>
              <div className="text-xs text-gray-500 font-mono">Running balance shown right-most</div>
            </div>
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] text-gray-500 uppercase tracking-wider">
                <tr className="border-b border-[var(--border)]">
                  <th className="px-5 py-3 font-medium">Date</th>
                  <th className="px-5 py-3 font-medium">Number</th>
                  <th className="px-5 py-3 font-medium">Type</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium text-right">Amount</th>
                  <th className="px-5 py-3 font-medium text-right">Change</th>
                  <th className="px-5 py-3 font-medium text-right">Balance</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody data-testid="stmt-timeline">
                {timeline.map((row) => (
                  <tr key={row.id} className="border-b border-[var(--border)] hover:bg-gray-50">
                    <td className="px-5 py-3 font-mono text-[12px] text-gray-600">{row.issue_date}</td>
                    <td className="px-5 py-3">
                      <Link to={`/app/documents/${row.id}`} className="font-mono text-[13px] hover:underline">{row.number}</Link>
                    </td>
                    <td className="px-5 py-3 text-gray-600">{DOC_TYPE_META[row.doc_type]?.short || row.doc_type}</td>
                    <td className="px-5 py-3"><StatusBadge status={row.status} /></td>
                    <td className="px-5 py-3 text-right num">{fmtMoney(row.amount, row.currency)}</td>
                    <td className={`px-5 py-3 text-right num ${row.delta > 0 ? "text-amber-700" : row.delta < 0 ? "text-green-700" : "text-gray-500"}`}>
                      {row.delta > 0 ? "+" : ""}{fmtMoney(row.delta, row.currency)}
                    </td>
                    <td className="px-5 py-3 text-right num font-semibold">{fmtMoney(row.running_balance, row.currency)}</td>
                    <td className="px-5 py-3 text-right">
                      <a
                        className="p-2 hover:bg-gray-100 rounded inline-flex"
                        title="Download PDF"
                        href="#"
                        onClick={async (e) => {
                          e.preventDefault();
                          try {
                            const res = await api.get(`/documents/${row.id}/pdf`, { responseType: "blob" });
                            const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                            const a = document.createElement("a");
                            a.href = url; a.download = `${row.number || "document"}.pdf`;
                            document.body.appendChild(a); a.click(); a.remove();
                            URL.revokeObjectURL(url);
                          } catch (err) { toast.error(formatApiError(err)); }
                        }}
                        data-testid={`stmt-pdf-${row.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Content>
    </>
  );
}
